import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { billsApi } from '../../services/smartBillApi';
import { generateMessage, openWhatsApp } from '../../services/whatsappTemplateService';
import './PendingWorks.css';
import { formatInIst } from '../../utils/istDateTime';

const WORK_STATUS_OPTIONS = [
  { value: 'under_processing', label: 'Under Processing', color: '#6366f1' },
  { value: 'pending', label: 'Pending', color: '#f59e0b' },
  { value: 'completed', label: 'Completed', color: '#10b981' },
  { value: 'cancelled', label: 'Cancelled', color: '#ef4444' },
];

const DATE_RANGE_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Previous Month' },
  { id: 'custom', label: 'From – To' },
];

const PAGE_SIZE = 200;
const MAX_PAGES = 50;

const formatYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIstDate = () => {
  const istDateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [year, month, day] = istDateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const resolveDateRange = (preset, fromDate, toDate) => {
  const today = getTodayIstDate();
  switch (preset) {
    case 'today': {
      const ymd = formatYmd(today);
      return { from: ymd, to: ymd };
    }
    case 'thisMonth': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: formatYmd(start), to: formatYmd(today) };
    }
    case 'lastMonth': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: formatYmd(start), to: formatYmd(end) };
    }
    case 'custom': {
      if (fromDate && toDate && fromDate > toDate) {
        return { from: toDate, to: fromDate };
      }
      return { from: fromDate || '', to: toDate || '' };
    }
    default:
      return { from: '', to: '' };
  }
};

const mapBill = (bill) => ({
  id: bill.id,
  billNo: bill.bill_number || bill.billNo,
  customerName: bill.customer_name || bill.customerName,
  customerPhone: bill.customer_phone || bill.customerPhone,
  billDate: bill.bill_date || bill.billDate,
  totalAmount: Number(bill.total_amount ?? bill.totalAmount ?? 0),
  workStatus: bill.work_status || bill.workStatus || 'pending',
  workRemark: bill.work_remark || bill.workRemark || '',
  isAdvance: !!(bill.is_advance ?? bill.isAdvance),
  services: bill.services || [],
  createdAt: bill.created_at || bill.createdAt,
  updatedAt: bill.updated_at || bill.updatedAt,
});

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatBillDate = (dateString) => formatInIst(dateString);

const PendingWorks = ({ onStartChat = null, showChatAction = false }) => {
  const [bills, setBills] = useState([]);
  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    under_processing: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
  });
  const [loading, setLoading] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [editForm, setEditForm] = useState({
    workStatus: '',
    workRemark: '',
    isAdvance: false,
  });
  const [filterStatus, setFilterStatus] = useState('pending');
  const [datePreset, setDatePreset] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const fetchSeqRef = useRef(0);

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    if (preset === 'custom') return;
    const { from, to } = resolveDateRange(preset, '', '');
    setFromDate(from);
    setToDate(to);
  };

  const handleFromDateChange = (value) => {
    setDatePreset('custom');
    setFromDate(value);
  };

  const handleToDateChange = (value) => {
    setDatePreset('custom');
    setToDate(value);
  };

  const fetchPendingWorks = useCallback(async () => {
    const requestId = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const { from, to } = resolveDateRange(datePreset, fromDate, toDate);
      const baseQuery = {
        search: searchTerm || '',
      };
      if (from) baseQuery.from = from;
      if (to) baseQuery.to = to;

      const summaryResponse = await billsApi.getAll({
        ...baseQuery,
        page: 1,
        limit: 1,
      });
      if (requestId !== fetchSeqRef.current) return;
      if (!summaryResponse.success) {
        throw new Error(summaryResponse.message || 'Failed to fetch pending works');
      }

      const statusCounts = summaryResponse.summary?.statusCounts || {};
      setSummaryCounts({
        total: Number(summaryResponse.summary?.totalRecords ?? 0) || 0,
        under_processing: Number(statusCounts.under_processing) || 0,
        pending: Number(statusCounts.pending) || 0,
        completed: Number(statusCounts.completed) || 0,
        cancelled: Number(statusCounts.cancelled) || 0,
      });

      const listQuery = {
        ...baseQuery,
        limit: PAGE_SIZE,
      };
      if (filterStatus !== 'all') {
        listQuery.work_status = filterStatus;
      }

      let filteredBills = [];
      let page = 1;
      let pages = 1;
      do {
        const response = await billsApi.getAll({ ...listQuery, page });
        if (requestId !== fetchSeqRef.current) return;
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch pending works');
        }
        filteredBills = filteredBills.concat((response.data || []).map(mapBill));
        pages = Math.min(Number(response.pagination?.pages) || 1, MAX_PAGES);
        page += 1;
      } while (page <= pages);

      if (filterStatus !== 'all') {
        filteredBills = filteredBills.filter((bill) => bill.workStatus === filterStatus);
      }

      filteredBills.sort((a, b) => new Date(b.billDate || 0) - new Date(a.billDate || 0));
      setBills(filteredBills);
    } catch (err) {
      if (requestId !== fetchSeqRef.current) return;
      console.error('Failed to load pending works:', err);
      toast.error(err.message || 'Failed to load pending works');
      setBills([]);
      setSummaryCounts({
        total: 0,
        under_processing: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
      });
    } finally {
      if (requestId === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [datePreset, filterStatus, fromDate, searchTerm, toDate]);

  useEffect(() => {
    fetchPendingWorks();
  }, [fetchPendingWorks]);

  const handleEdit = (bill) => {
    setEditingBill(bill);
    setEditForm({
      workStatus: bill.workStatus || 'pending',
      workRemark: bill.workRemark || '',
      isAdvance: !!bill.isAdvance,
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'isAdvance' && value && prev.workStatus === 'completed') {
        next.workStatus = 'pending';
      }
      return next;
    });
  };

  const handleUpdate = async () => {
    if (!editingBill) return;

    try {
      setLoading(true);
      await billsApi.update(editingBill.id, {
        work_status: editForm.workStatus,
        work_remark: editForm.workRemark,
        is_advance: !!editForm.isAdvance,
        isAdvance: !!editForm.isAdvance,
      });

      toast.success('Work status updated successfully!');
      setEditingBill(null);
      setEditForm({ workStatus: '', workRemark: '', isAdvance: false });
      await fetchPendingWorks();
    } catch (err) {
      console.error('Failed to update work status:', err);
      toast.error(err.message || 'Failed to update work status');
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (bill) => {
    if (!bill.customerPhone) {
      toast.error('Customer phone number is required to make a call');
      return;
    }

    const phone = bill.customerPhone.replace(/\D/g, '').slice(0, 10);
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    const countryCode = process.env.REACT_APP_WHATSAPP_COUNTRY_CODE || '91';
    const telUrl = `tel:+${countryCode}${phone}`;
    window.location.href = telUrl;
  };

  const getUserLoginBaseUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:3001';
    if (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in')) {
      return window.location.origin;
    }
    return 'http://localhost:3001';
  };

  const handleWhatsAppShare = (bill) => {
    if (!bill.customerPhone) {
      toast.error('Customer phone number is required');
      return;
    }

    const phone = bill.customerPhone.replace(/\D/g, '');
    if (phone.length !== 10) {
      toast.error('Invalid phone number');
      return;
    }
    const defaultPassword = phone.slice(-4);
    const baseUrl = getUserLoginBaseUrl();
    const chatLink = `${baseUrl}/user-login?phone=${phone}&password=${defaultPassword}&redirect=${encodeURIComponent('/user/coupons')}`;
    const statusLabel =
      WORK_STATUS_OPTIONS.find((option) => option.value === bill.workStatus)?.label ||
      bill.workStatus ||
      'Pending';
    const servicesList =
      bill.services && bill.services.length > 0
        ? bill.services
            .map((service) => `• ${service.display_name || service.service_name || service.name || 'N/A'}`)
            .join('\n')
        : '• Service update';
    const remarks = [
      bill.workRemark,
      `Coupons link: ${chatLink}`,
      'One-click login → see your coupons → Redeem.',
    ]
      .filter(Boolean)
      .join('\n');

    const message = generateMessage('workStatus', {
      billNo: bill.billNo || 'N/A',
      customerName: bill.customerName || 'Customer',
      billDate: formatBillDate(bill.billDate),
      servicesList,
      status: statusLabel,
      remarks,
      serviceNames:
        bill.services && bill.services.length > 0
          ? bill.services
              .map((service) => service.display_name || service.service_name || service.name || 'N/A')
              .join(', ')
          : 'your service',
    });

    openWhatsApp(phone, message);
  };

  const handleStartChat = (bill) => {
    if (typeof onStartChat !== 'function') return;
    onStartChat(bill);
  };

  const getStatusBadge = (billOrStatus) => {
    const isAdvance =
      typeof billOrStatus === 'object' && billOrStatus
        ? !!billOrStatus.isAdvance
        : false;
    const status =
      typeof billOrStatus === 'object' && billOrStatus
        ? billOrStatus.workStatus
        : billOrStatus;

    if (isAdvance) {
      return (
        <span className="status-badge" style={{ backgroundColor: '#2563eb20', color: '#1d4ed8' }}>
          ADVANCE
        </span>
      );
    }

    const option = WORK_STATUS_OPTIONS.find((opt) => opt.value === status);
    if (!option) return null;

    return (
      <span className="status-badge" style={{ backgroundColor: `${option.color}20`, color: option.color }}>
        {option.label}
      </span>
    );
  };

  return (
    <div className="pending-works-page">
      <header className="pending-works-header">
        <div className="header-content">
          <div>
            <h1>Pending Works Management</h1>
            <p>Track and manage all pending works and their status updates.</p>
          </div>
          <button className="btn btn-primary" onClick={fetchPendingWorks} disabled={loading}>
            <i className="fas fa-sync"></i> Refresh
          </button>
        </div>
      </header>

      <div className="pending-works-filters">
        <div className="filter-group">
          <label>Date</label>
          <div className="date-filter-row">
            {DATE_RANGE_OPTIONS.map((range) => (
              <button
                key={range.id}
                type="button"
                className={`date-range-chip ${datePreset === range.id ? 'active' : ''}`}
                onClick={() => applyDatePreset(range.id)}
              >
                {range.label}
              </button>
            ))}
          </div>
          <div className="date-range-inputs">
            <div className="date-range-field">
              <label htmlFor="pending-works-from-date">From</label>
              <input
                id="pending-works-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-range-field">
              <label htmlFor="pending-works-to-date">To</label>
              <input
                id="pending-works-to-date"
                type="date"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                className="date-input"
              />
            </div>
          </div>
        </div>
        <div className="filter-group">
          <label>Filter by Status</label>
          <div className="status-filter-chips">
            <button
              className={`status-chip ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All
            </button>
            {WORK_STATUS_OPTIONS.map((status) => (
              <button
                key={status.value}
                className={`status-chip ${filterStatus === status.value ? 'active' : ''}`}
                onClick={() => setFilterStatus(status.value)}
                style={{
                  borderColor: filterStatus === status.value ? status.color : '#d1d5db',
                  backgroundColor: filterStatus === status.value ? `${status.color}15` : '#fff',
                  color: filterStatus === status.value ? status.color : '#4b5563',
                }}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by customer name, phone, or bill number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="pending-works-summary">
        <div className="summary-card">
          <span className="summary-label">
            {datePreset === 'all' && !fromDate && !toDate ? 'Total Works All Dates' : 'Total Works'}
          </span>
          <span className="summary-value">{summaryCounts.total}</span>
        </div>
        {WORK_STATUS_OPTIONS.map((status) => {
          const count = summaryCounts[status.value] || 0;
          return (
            <div key={status.value} className="summary-card">
              <span className="summary-label">{status.label}</span>
              <span className="summary-value" style={{ color: status.color }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>

      {loading && bills.length === 0 ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading pending works...</p>
        </div>
      ) : bills.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>No pending works found</h3>
          <p>All works are completed or no works match your filters.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="pending-works-table-wrapper">
            <table className="pending-works-table">
              <thead>
                <tr>
                  <th>Bill No</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Services</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => (
                  <tr key={bill.id}>
                    <td>
                      <strong>{bill.billNo || '—'}</strong>
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">{bill.customerName || '—'}</div>
                        <div 
                          className="customer-phone clickable-phone" 
                          onClick={() => bill.customerPhone && handleCall(bill)}
                          title="Click to call"
                        >
                          {bill.customerPhone || '—'}
                        </div>
                      </div>
                    </td>
                    <td>{formatBillDate(bill.billDate)}</td>
                    <td>
                      <div className="services-list">
                        {bill.services && bill.services.length > 0 ? (
                          bill.services.slice(0, 2).map((service, idx) => (
                            <span key={idx} className="service-tag">
                              {service.display_name || service.service_name || service.name || 'N/A'}
                            </span>
                          ))
                        ) : (
                          <span className="no-services">No services</span>
                        )}
                        {bill.services && bill.services.length > 2 && (
                          <span className="more-services">+{bill.services.length - 2} more</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{formatCurrency(bill.totalAmount)}</strong>
                    </td>
                    <td>{getStatusBadge(bill)}</td>
                    <td>
                      <div className="remarks-cell">
                        {bill.workRemark ? (
                          <div className="remarks-text">
                            {bill.workRemark}
                          </div>
                        ) : (
                          <span className="no-remarks">—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn-icon btn-call"
                          onClick={() => handleCall(bill)}
                          title="Call customer"
                        >
                          <i className="fas fa-phone"></i>
                        </button>
                        {showChatAction && (
                          <button
                            className="btn-icon btn-chat"
                            onClick={() => handleStartChat(bill)}
                            title="Start Chat"
                          >
                            <i className="fas fa-comments"></i>
                          </button>
                        )}
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(bill)}
                          title="Edit Status & Remarks"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn-icon btn-whatsapp"
                          onClick={() => handleWhatsAppShare(bill)}
                          title="Share via WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="pending-works-mobile-cards">
            {bills.map((bill) => (
              <div key={bill.id} className="pending-works-mobile-card">
                <div className="mobile-card-header">
                  <div>
                    <div className="mobile-card-bill-no">{bill.billNo || '—'}</div>
                    <div className="mobile-card-status">{getStatusBadge(bill)}</div>
                  </div>
                  <div>
                    <strong style={{ fontSize: '18px', color: '#111827' }}>
                      {formatCurrency(bill.totalAmount)}
                    </strong>
                  </div>
                </div>

                <div className="mobile-card-row">
                  <div className="mobile-card-label">Customer</div>
                  <div className="mobile-card-value">
                    <div className="customer-name">{bill.customerName || '—'}</div>
                    <div 
                      className="customer-phone clickable-phone" 
                      style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}
                      onClick={() => bill.customerPhone && handleCall(bill)}
                      title="Click to call"
                    >
                      {bill.customerPhone || '—'}
                    </div>
                  </div>
                </div>

                <div className="mobile-card-row">
                  <div className="mobile-card-label">Date</div>
                  <div className="mobile-card-value">{formatBillDate(bill.billDate)}</div>
                </div>

                <div className="mobile-card-row">
                  <div className="mobile-card-label">Services</div>
                  <div className="mobile-card-value">
                    <div className="services-list">
                      {bill.services && bill.services.length > 0 ? (
                        bill.services.slice(0, 3).map((service, idx) => (
                          <span key={idx} className="service-tag">
                            {service.display_name || service.service_name || service.name || 'N/A'}
                          </span>
                        ))
                      ) : (
                        <span className="no-services">No services</span>
                      )}
                      {bill.services && bill.services.length > 3 && (
                        <span className="more-services">+{bill.services.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </div>

                {bill.workRemark && (
                  <div className="mobile-card-row">
                    <div className="mobile-card-label">Remarks</div>
                    <div className="mobile-card-value" style={{ fontSize: '13px', color: '#374151' }}>
                      {bill.workRemark}
                    </div>
                  </div>
                )}

                <div className="mobile-card-actions">
                  <button
                    className="btn-icon btn-call"
                    onClick={() => handleCall(bill)}
                    title="Call customer"
                  >
                    <i className="fas fa-phone"></i> Call
                  </button>
                  {showChatAction && (
                    <button
                      className="btn-icon btn-chat"
                      onClick={() => handleStartChat(bill)}
                      title="Start Chat"
                    >
                      <i className="fas fa-comments"></i> Chat
                    </button>
                  )}
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => handleEdit(bill)}
                    title="Edit Status & Remarks"
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    className="btn-icon btn-whatsapp"
                    onClick={() => handleWhatsAppShare(bill)}
                    title="Share via WhatsApp"
                  >
                    <i className="fab fa-whatsapp"></i> WhatsApp
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {editingBill && (
        <div className="edit-modal">
          <div className="edit-modal-backdrop" onClick={() => setEditingBill(null)}></div>
          <div className="edit-modal-content">
            <h3>Update Work Status & Remarks</h3>
            <div className="edit-form">
              <div className="form-group">
                <label>Bill Information</label>
                <div className="bill-info">
                  <div>
                    <strong>Bill No:</strong> {editingBill.billNo}
                  </div>
                  <div>
                    <strong>Customer:</strong> {editingBill.customerName}
                  </div>
                  <div>
                    <strong>Phone:</strong> {editingBill.customerPhone}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Work Status *</label>
                <div className="status-options">
                  {WORK_STATUS_OPTIONS.map((status) => (
                    <label key={status.value} className="status-option">
                      <input
                        type="radio"
                        name="workStatus"
                        value={status.value}
                        checked={editForm.workStatus === status.value}
                        onChange={(e) => handleEditChange('workStatus', e.target.value)}
                      />
                      <span
                        className="status-option-label"
                        style={{
                          borderColor: editForm.workStatus === status.value ? status.color : '#d1d5db',
                          backgroundColor: editForm.workStatus === status.value ? `${status.color}15` : '#fff',
                          color: editForm.workStatus === status.value ? status.color : '#4b5563',
                        }}
                      >
                        {status.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="advance-edit-toggle">
                  <input
                    type="checkbox"
                    checked={!!editForm.isAdvance}
                    onChange={(e) => handleEditChange('isAdvance', e.target.checked)}
                  />
                  <span>
                    <strong>Advance amount</strong>
                    <small>Excluded from counter sales until Completed</small>
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>Remarks / Notes</label>
                <textarea
                  value={editForm.workRemark}
                  onChange={(e) => handleEditChange('workRemark', e.target.value)}
                  placeholder="Enter any remarks, pending works, or notes about the customer..."
                  rows={4}
                ></textarea>
                <small className="form-hint">
                  ⓘ Use this field to note any pending work, follow-ups, or important information.
                </small>
              </div>
            </div>

            <div className="edit-modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingBill(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
                {loading ? 'Updating...' : 'Update Status'}
              </button>
              <button
                className="btn btn-success"
                onClick={() => {
                  handleUpdate().then(() => {
                    if (editingBill) {
                      setTimeout(() => handleWhatsAppShare({ ...editingBill, ...editForm }), 500);
                    }
                  });
                }}
                disabled={loading}
              >
                <i className="fab fa-whatsapp"></i> Update & Share
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingWorks;

